import { useQuery } from '@tanstack/react-query';
import type { TaxGroup } from '@masamune/shared';
import { EmptyState } from '../../../components/ui/EmptyState';
import { fiscoGruppoQuery } from './fiscoApi';
import { Caricamento, Fascia, Frase, Intestazione, Piega, TabellaPagate, TabellaTasse } from './parts';

/**
 * Le viste per tipo che hanno la stessa forma: F24 dipendenti, Imposte della
 * società (IRES e IRAP), Altri versamenti. Frase, fascia di 2-3 somme, le
 * tasse da pagare, le pagate in un blocco chiuso (proposta § 3.3).
 * IVA e Debiti a rate hanno blocchi loro e stanno in file a parte.
 */
const NOMI: Record<'F24_DIPENDENTI' | 'SOCIETA' | 'ALTRI', { aperte: string; pagate: string; vuoto: string }> = {
  F24_DIPENDENTI: {
    aperte: 'F24 dei dipendenti da pagare',
    pagate: 'gli F24 dei dipendenti già pagati, di tutti gli anni',
    vuoto: 'Nessun F24 dei dipendenti da pagare, con i dati di oggi.',
  },
  SOCIETA: {
    aperte: 'Imposte della società da pagare',
    pagate: 'le imposte della società già pagate, di tutti gli anni',
    vuoto: 'Nessuna imposta della società da pagare, con i dati di oggi.',
  },
  ALTRI: {
    aperte: 'Altri versamenti al fisco da fare',
    pagate: 'gli altri versamenti al fisco già fatti, di tutti gli anni',
    vuoto: 'Nessun altro versamento al fisco da fare, con i dati di oggi.',
  },
};

export function VistaTipo({ group, canWrite, onVai }: { group: 'F24_DIPENDENTI' | 'SOCIETA' | 'ALTRI'; canWrite: boolean; onVai: (g: TaxGroup) => void }) {
  const q = useQuery(fiscoGruppoQuery(group));
  const d = q.data;
  const nomi = NOMI[group];
  if (q.isLoading && !d) return <Caricamento />;
  if (!d) return <EmptyState icon="percent" title="Tasse non disponibili" description="Non riesco a leggere le tasse in questo momento. Riprova tra poco." />;

  return (
    <>
      <Frase testo={d.frase} />
      <Fascia voci={d.fascia} />
      <section className="fis-gruppo mt-5">
        {/* Niente conteggio qui: quante sono lo dice già la fascia (un numero, una volta). */}
        <Intestazione label={nomi.aperte} />
        {d.righe.length === 0
          ? <div className="fis-vuoto">{nomi.vuoto}</div>
          : <TabellaTasse righe={d.righe} canWrite={canWrite} onVai={onVai} />}
      </section>
      {d.pagate.length > 0 && (
        <Piega label={nomi.pagate} n={d.pagate.length}>
          <TabellaPagate righe={d.pagate} canWrite={canWrite} />
        </Piega>
      )}
    </>
  );
}
